import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useLocale } from "@/hooks/useLocale";
import { useSendChatMessageMutation } from "@/services/(my-space)/api.chat";
import {
  useMsCopyChatAttachmentsMutation,
  useMsCreateTaskMutation,
  useMsGetAllTasksQuery,
  useMsGetBoardMembersByBoardQuery,
  useMsGetMyProjectsQuery,
  useMsGetProjectBoardQuery,
} from "@/services/(my-space)/api.tasks";
import { MS_TASKS_PATHS } from "@/routes/my-space/routes.tasks";
import type { IChatConversation, IChatMessage } from "@/types/(my-space)/chat.interface";
import {
  ENUM_BOARD_METHODOLOGY,
  ENUM_SPRINT_STATUS,
  ENUM_TASK_PRIORITY,
  ENUM_TASK_TYPE,
} from "@/utils/enums/tasks.enum";
import { getErrorMessage } from "@/utils/error.utils";
import { conversationTitle, messageContentPreview, personName } from "../_lib/chat-labels";
import { newClientMessageId } from "../_lib/chat-utils";
import {
  messagePlainText,
  suggestTicketTitle,
  ticketDescriptionHtml,
} from "../_lib/ticket-from-chat";

const TYPES = [ENUM_TASK_TYPE.TASK, ENUM_TASK_TYPE.BUG, ENUM_TASK_TYPE.STORY] as const;
const PRIORITIES = [
  ENUM_TASK_PRIORITY.LOW,
  ENUM_TASK_PRIORITY.MEDIUM,
  ENUM_TASK_PRIORITY.HIGH,
  ENUM_TASK_PRIORITY.URGENT,
] as const;
const UNASSIGNED = "none";

/**
 * A project ticket from the selected chat messages: project, title, type,
 * priority, assignee and due date; the messages become its description
 * (quoted, oldest first, with who and when). Optionally shares the new
 * ticket's card back into the chat.
 */
export const CreateTicketDialog = ({
  open,
  onClose,
  onCreated,
  messages,
  conversation,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  messages: IChatMessage[];
  conversation: IChatConversation | undefined;
}) => {
  const { t } = useTranslation("chat");
  const { formatDate } = useLocale();
  const navigate = useNavigate();
  const projects = useMsGetMyProjectsQuery(undefined, { skip: !open });
  const projectList = projects.data?.data ?? [];

  const [projectId, setProjectId] = useState<number | null>(null);
  const [title, setTitle] = useState("");
  const [type, setType] = useState<ENUM_TASK_TYPE>(ENUM_TASK_TYPE.TASK);
  const [priority, setPriority] = useState<ENUM_TASK_PRIORITY>(ENUM_TASK_PRIORITY.MEDIUM);
  const [assigneeId, setAssigneeId] = useState<string>(UNASSIGNED);
  const [dueDate, setDueDate] = useState("");
  const [notes, setNotes] = useState("");
  const [share, setShare] = useState(true);

  const board = useMsGetProjectBoardQuery({ projectId: projectId ?? 0 }, { skip: !open || projectId == null });
  const boardId = board.data?.data?.id;
  const isScrum = board.data?.data?.methodology === ENUM_BOARD_METHODOLOGY.SCRUM;
  // A Scrum board shows only its active sprint: a ticket outside it would sit
  // in the backlog, unseen. Found as the board page finds it (no sprints
  // endpoint in My Space) — the same cached query.
  const boardTasks = useMsGetAllTasksQuery({ boardId: boardId ?? 0 }, { skip: !open || !boardId || !isScrum });
  const activeSprintId = isScrum
    ? boardTasks.data?.data?.find((task) => task.sprint?.status === ENUM_SPRINT_STATUS.ACTIVE)?.sprint?.id
    : undefined;
  const members = useMsGetBoardMembersByBoardQuery({ boardId: boardId ?? 0 }, { skip: !boardId });
  const [createTask, { isLoading: creatingTask }] = useMsCreateTaskMutation();
  const [copyChatFiles, { isLoading: copyingFiles }] = useMsCopyChatAttachmentsMutation();
  const creating = creatingTask || copyingFiles;
  const [sendMessage] = useSendChatMessageMutation();

  const ordered = useMemo(() => [...messages].sort((a, b) => a.id - b.id), [messages]);
  const contentLabel = (m: IChatMessage) =>
    messageContentPreview(t, { ...m, attachmentCount: m.attachments.length });
  const chatName = conversation ? conversationTitle(t, conversation) : "";

  // Each opening starts from the selection: a suggested title, defaults,
  // and the only project preselected.
  useEffect(() => {
    if (!open) return;
    setTitle(suggestTicketTitle(ordered, contentLabel, t("ticket_from_chat.default_title", { chat: chatName })));
    setType(ENUM_TASK_TYPE.TASK);
    setPriority(ENUM_TASK_PRIORITY.MEDIUM);
    setAssigneeId(UNASSIGNED);
    setDueDate("");
    setNotes("");
    setShare(true);
  }, [open]);
  useEffect(() => {
    if (open && projectId == null && projectList.length === 1) setProjectId(projectList[0].id);
  }, [open, projectId, projectList]);
  // A different project has different members.
  useEffect(() => setAssigneeId(UNASSIGNED), [projectId]);

  // Wait for a Scrum board's sprint, or the ticket would miss it.
  const canCreate =
    !!boardId && !!title.trim() && !creating && ordered.length > 0 && !(isScrum && boardTasks.isFetching);

  const create = async () => {
    if (!boardId || !canCreate) return;
    try {
      // The messages' files become the ticket's own attachments (copies, so
      // deleting one never touches the other); pictures also show inline.
      const chatFileIds = [
        ...new Set(ordered.flatMap((m) => (m.attachments ?? []).map((a) => a.id).filter((id): id is number => !!id))),
      ].sort((a, b) => a - b);
      const copiedUrls = new Map<number, string>();
      const attachmentIds: number[] = [];
      if (chatFileIds.length) {
        try {
          const copied = (await copyChatFiles({ chatAttachmentIds: chatFileIds }).unwrap()).data ?? [];
          // Returned in chat-file id order.
          copied.forEach((c, i) => {
            attachmentIds.push(c.id);
            if (chatFileIds[i] != null && c.fileUrl) copiedUrls.set(chatFileIds[i], c.fileUrl);
          });
        } catch (e) {
          // The ticket still links the chat's files.
          toast.error(getErrorMessage(e, t("ticket_from_chat.files_failed")));
        }
      }
      const description = ticketDescriptionHtml(
        ordered,
        {
          senderName: (m) => personName(t, m.sender),
          formatTime: (iso) => formatDate(new Date(iso), "PPp"),
          contentLabel,
          fromChat: t("ticket_from_chat.from_chat", { chat: chatName }),
          fileUrl: (a) => (a.id ? copiedUrls.get(a.id) : undefined),
        },
        notes,
      );
      const res = await createTask({
        boardId,
        title: title.trim(),
        description,
        type,
        priority,
        ...(assigneeId !== UNASSIGNED ? { assigneeId: Number(assigneeId) } : {}),
        ...(dueDate ? { dueDate } : {}),
        ...(activeSprintId ? { sprintId: activeSprintId } : {}),
        ...(attachmentIds.length ? { attachmentIds } : {}),
      }).unwrap();
      const task = res.data;
      if (share && conversation?.canSend) {
        // Its card in this chat; a failure here doesn't undo the ticket.
        sendMessage({
          conversationId: conversation.id,
          clientMessageId: newClientMessageId(),
          message: { type: "shortcut", shortcut: { entity: { type: "task", id: task.id } } },
        })
          .unwrap()
          .catch((e) => toast.error(getErrorMessage(e, t("errors.send"))));
      }
      const path = projectId != null ? MS_TASKS_PATHS.DETAIL(projectId, task.id) : MS_TASKS_PATHS.TASK_REDIRECT(task.id);
      toast.success(
        (tt) => (
          <span className="flex items-center gap-3">
            {t("ticket_from_chat.created")}
            <Button
              variant="outline"
              size="xs"
              onClick={() => {
                toast.dismiss(tt.id);
                navigate(path);
              }}
            >
              {t("ticket_from_chat.open")}
            </Button>
          </span>
        ),
        { duration: 6000 },
      );
      onCreated();
    } catch (e) {
      toast.error(getErrorMessage(e, t("ticket_from_chat.failed")));
    }
  };

  const noProjects = projects.isSuccess && projectList.length === 0;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !creating && onClose()}>
      <DialogContent className="flex max-h-[90vh] flex-col sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("ticket_from_chat.title")}</DialogTitle>
          <DialogDescription>{t("ticket_from_chat.subtitle", { count: ordered.length })}</DialogDescription>
        </DialogHeader>

        {noProjects ? (
          <p className="rounded-md bg-muted px-4 py-6 text-center text-sm text-muted-foreground">
            {t("ticket_from_chat.no_projects")}
          </p>
        ) : (
          <div className="flex min-h-0 flex-col gap-4 overflow-y-auto pe-1">
            <div className="flex flex-col gap-1.5">
              <Label>{t("ticket_from_chat.project")}</Label>
              <Select
                value={projectId != null ? String(projectId) : undefined}
                onValueChange={(v) => setProjectId(Number(v))}
                disabled={projects.isLoading}
              >
                <SelectTrigger aria-label={t("ticket_from_chat.project")}>
                  <SelectValue placeholder={t("ticket_from_chat.pick_project")} />
                </SelectTrigger>
                <SelectContent>
                  {projectList.map((p) => (
                    <SelectItem key={p.id} value={String(p.id)}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="ticket-title">{t("ticket_from_chat.ticket_title")}</Label>
              <Input id="ticket-title" value={title} maxLength={255} onChange={(e) => setTitle(e.target.value)} />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <Label>{t("ticket_from_chat.type")}</Label>
                <Select value={type} onValueChange={(v) => setType(v as ENUM_TASK_TYPE)}>
                  <SelectTrigger aria-label={t("ticket_from_chat.type")}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TYPES.map((v) => (
                      <SelectItem key={v} value={v}>
                        {t(`ticket_from_chat.types.${v}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>{t("ticket_from_chat.priority")}</Label>
                <Select value={priority} onValueChange={(v) => setPriority(v as ENUM_TASK_PRIORITY)}>
                  <SelectTrigger aria-label={t("ticket_from_chat.priority")}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRIORITIES.map((v) => (
                      <SelectItem key={v} value={v}>
                        {t(`ticket_from_chat.priorities.${v}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>{t("ticket_from_chat.assignee")}</Label>
                <Select value={assigneeId} onValueChange={setAssigneeId} disabled={!boardId}>
                  <SelectTrigger aria-label={t("ticket_from_chat.assignee")}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={UNASSIGNED}>{t("ticket_from_chat.unassigned")}</SelectItem>
                    {(members.data?.data ?? []).map((u) => (
                      <SelectItem key={u.id} value={String(u.id)}>
                        {personName(t, { firstName: u.firstName ?? "", lastName: u.lastName ?? "" })}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="ticket-due">{t("ticket_from_chat.due_date")}</Label>
                <Input id="ticket-due" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="ticket-notes">{t("ticket_from_chat.notes")}</Label>
              <Textarea
                id="ticket-notes"
                value={notes}
                rows={2}
                placeholder={t("ticket_from_chat.notes_placeholder")}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>

            {/* What the description will quote. */}
            <div className="flex flex-col gap-1.5">
              <Label>{t("ticket_from_chat.messages", { count: ordered.length })}</Label>
              <ul className="flex max-h-44 flex-col gap-1.5 overflow-y-auto rounded-md border border-border bg-muted/40 p-2">
                {ordered.map((m) => (
                  <li key={m.id} className="border-s-2 border-primary/40 ps-2 text-xs">
                    <span className="font-semibold text-foreground">{personName(t, m.sender)}</span>
                    <span className="text-muted-foreground"> · {formatDate(new Date(m.createdAt), "PPp")}</span>
                    <p dir="auto" className="line-clamp-3 whitespace-pre-wrap break-words text-muted-foreground">
                      {messagePlainText(m, contentLabel)}
                    </p>
                  </li>
                ))}
              </ul>
            </div>

            {conversation?.canSend ? (
              <label className="flex items-center gap-2 text-sm text-foreground">
                <Checkbox checked={share} onCheckedChange={(v) => setShare(v === true)} />
                {t("ticket_from_chat.share")}
              </label>
            ) : null}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={creating}>
            {t("ticket_from_chat.cancel")}
          </Button>
          <Button variant="primary" onClick={() => void create()} disabled={!canCreate} isLoading={creating}>
            {t("ticket_from_chat.create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
